<script setup lang="ts" generic="T extends string | number | null">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import type { TabPanel } from '@/common/utils/types';
import { ref } from 'vue';

interface Props {
  tabs: TabPanel<T>[];
  modelValue: T | null;
  underline?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: T): void;
}

withDefaults(defineProps<Props>(), { underline: true });
const emit = defineEmits<Emits>();

const listRef = ref<HTMLUListElement | null>(null);
const itemRefs = ref<(HTMLElement | null)[]>([]);

const setItemRef = (el: HTMLElement | null, index: number) => {
  itemRefs.value[index] = el;
};

// SlidingTabs 등 상위 컴포넌트가 인디케이터 위치 계산에 쓰도록 노출.
defineExpose({ listRef, itemRefs });
</script>

<template>
  <ul
    ref="listRef"
    :class="['base-tabs', { 'base-tabs--no-underline': !underline }]"
  >
    <template
      v-for="(tab, index) in tabs"
      :key="tab.value ?? index"
    >
      <span
        v-if="tab.dividerBefore"
        class="base-tabs__divider"
        aria-hidden="true"
      />
      <li
        :ref="(el) => setItemRef(el as HTMLElement | null, index)"
        :class="['base-tabs__item', { 'base-tabs__item--active': modelValue === tab.value }]"
      >
        <base-button
          tag="button"
          type="button"
          :data-text="tab.text"
          :class="['base-tabs__button', { active: modelValue === tab.value }]"
          @click.stop="emit('update:modelValue', tab.value)"
        >
          {{ tab.text }}
        </base-button>
      </li>
    </template>
    <slot />
  </ul>
</template>

<style scoped lang="scss">
.base-tabs {
  display: flex;
  position: relative;
  gap: 4px;
  align-items: flex-start;
  align-self: stretch;
  height: 36px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-gray-05-03);

  &__item {
    display: flex;
    position: relative;
    align-items: center;
    align-self: stretch;
    justify-content: center;
    min-width: 48px;
    cursor: pointer;
  }

  // 서브그룹 구분선(예: Batch 앞의 | )
  &__divider {
    align-self: center;
    width: 1px;
    height: 12px;
    margin: 0 8px;
    background: var(--color-gray-05-03);
  }

  &__button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 4px 8px;
    cursor: pointer;
    background: transparent;
    font-weight: 500 !important;
    color: var(--color-gray-03-09);

    @include v2-typo('body1');

    &.active {
      font-weight: 700 !important;
      color: var(--color-gray-00-12) !important;
    }

    // 활성 전환 시 글자 두께 증가(500 -> 700)로 형제 탭이 밀리는 것을 방지.
    &::after {
      visibility: hidden;
      height: 0;
      overflow: hidden;
      pointer-events: none;
      user-select: none;
      font-weight: 700;
      content: attr(data-text);
    }
  }

  &__item:hover,
  &__item:active {
    .base-tabs__button {
      color: var(--color-gray-01-11);
    }
  }

  // 기본(정적) 밑줄. underline=false면 상위(SlidingTabs)의 슬라이딩 인디케이터가 대신하므로 숨긴다.
  &__item--active::after {
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 2px;
    background: var(--color-gray-00-12);
    content: '';
  }

  &--no-underline &__item--active::after {
    display: none;
  }
}
</style>
